/**
 * PageHeader — shared page-level header component.
 * Renders an optional tenant logo, a title, optional subtitle, optional status badge,
 * and optional action area. All colors pull from the MUI theme — no hardcoded hex.
 *
 * The logo is fetched from the branding API endpoint (GET /api/branding/logo) via
 * authenticated fetch and rendered as an object URL. This works with private containers.
 */

import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { authFetch } from '../../services/authFetch';

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  /** Optional status indicator (e.g., a StatusBadge). Placed inline with the title. */
  status?: React.ReactNode;
  /** Optional primary action area (e.g., a Button). Aligned to the right. */
  action?: React.ReactNode;
}

export const PageHeader: React.FC<PageHeaderProps> = ({ title, subtitle, status, action }) => {
  const theme = useTheme();
  const [logoSrc, setLogoSrc] = useState<string | null>(null);

  useEffect(() => {
    let objectUrl: string | null = null;

    const loadLogo = async () => {
      try {
        const resp = await authFetch('/branding/logo');
        if (!resp.ok) return; // No logo or error — silently skip
        const blob = await resp.blob();
        if (blob.size === 0) return;
        objectUrl = URL.createObjectURL(blob);
        setLogoSrc(objectUrl);
      } catch {
        // Silently fail — no logo is fine
      }
    };

    loadLogo();

    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, []);

  return (
    <Box
      component="header"
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderBottom: `1px solid ${theme.palette.divider}`,
        px: 3,
        py: 2,
        mb: 0,
        minHeight: 84,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
        {logoSrc && (
          <Box
            component="img"
            src={logoSrc}
            alt={title}
            sx={{
              height: 36,
              maxWidth: 48,
              objectFit: 'contain',
              flexShrink: 0,
            }}
          />
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography
              variant="h5"
              component="h1"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
                mb: subtitle ? 0.5 : 0,
                lineHeight: 1.3,
              }}
            >
              {title}
            </Typography>
            {status && <Box sx={{ mb: subtitle ? 0.5 : 0 }}>{status}</Box>}
          </Box>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>

      {action && (
        <Box sx={{ flexShrink: 0 }}>{action}</Box>
      )}
    </Box>
  );
};
